import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import Input from '../../../shared/components/ui/Input/Input';
import { CompanyStats } from '../../../store/responseTypes/company';

interface CustomerPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: CompanyStats[];
  initialSelectedIds: Set<string>;
  onApply: (ids: Set<string>) => void;
}

const CustomerPickerModal: React.FC<CustomerPickerModalProps> = ({
  isOpen,
  onClose,
  stats,
  initialSelectedIds,
  onApply,
}) => {
  const [draft, setDraft] = useState<Set<string>>(new Set(initialSelectedIds));
  const [query, setQuery] = useState('');

  // Re-seed the draft each time the modal opens — we intentionally do NOT
  // include initialSelectedIds in the deps so the draft can diverge while
  // the modal is open without surprise rewinds.
  useEffect(() => {
    if (isOpen) {
      setDraft(new Set(initialSelectedIds));
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = [...stats].sort((a, b) =>
      a.companyName.localeCompare(b.companyName),
    );
    if (!q) return base;
    return base.filter((s) => s.companyName.toLowerCase().includes(q));
  }, [stats, query]);

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setDraft((prev) => {
      const next = new Set(prev);
      filtered.forEach((s) => next.add(s.companyId));
      return next;
    });
  };

  const clearAllFiltered = () => {
    setDraft((prev) => {
      const next = new Set(prev);
      filtered.forEach((s) => next.delete(s.companyId));
      return next;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx="1rem">
        <ModalHeader>Pick customers</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Input
              name="customer-picker-search"
              placeholder="Search customers…"
              onChange={setQuery}
            />
            <HStack spacing={2}>
              <Button size="xs" variant="outline" onClick={selectAllFiltered}>
                {query ? 'Add matching' : 'Add all'}
              </Button>
              <Button size="xs" variant="outline" onClick={clearAllFiltered}>
                {query ? 'Remove matching' : 'Remove all'}
              </Button>
              <Spacer />
              <Text fontSize="xs" color="gray.500">
                {filtered.length} shown · {draft.size} selected
              </Text>
            </HStack>
            <Box maxH="50vh" overflowY="auto" borderTop="1px solid" borderColor="gray.100">
              {filtered.length === 0 ? (
                <Text fontSize="sm" color="gray.400" py={4} textAlign="center">
                  No customers match “{query}”.
                </Text>
              ) : (
                filtered.map((s) => (
                  <HStack
                    key={s.companyId}
                    py={2}
                    px={2}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <Checkbox
                      isChecked={draft.has(s.companyId)}
                      onChange={() => toggle(s.companyId)}
                    >
                      {s.companyName}
                    </Checkbox>
                  </HStack>
                ))
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} w="100%">
            <Text fontSize="sm" color="gray.500">
              {draft.size} selected
            </Text>
            <Spacer />
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onApply(draft);
                onClose();
              }}
            >
              Apply
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomerPickerModal;
